import React, { FunctionComponent } from 'react';
import Modal from 'react-modal';
import ScrollLock from 'react-scrolllock';
import { useMobile } from '../../../helpers/devices';
import zIndexes from '../../../styles/zIndexes';
import { GridItem, Icon } from '../../atoms';
import { CloseButton, ContentWrapper, InnerModal, Overlay } from './ModalWindow.style';
import { ModalWindowProps } from '../../../types';

const MAX_Z_INDEX = 9999999999;

if (Modal.defaultStyles.content) {
  Modal.defaultStyles.content.position = 'static';
}

/**
 * A ModalWindow component, leveraging React Modal for rendering modals with enhanced accessibility
 * and styling customization. This component is designed to be flexible for various use cases, with
 * support for mobile responsiveness, custom z-indexes, and a close button.
 *
 * # Usage
 * This component is intended to be used in React applications where modal functionality is required.
 * It supports a range of props for customization, including isOpen, setIsOpen, children, appElementId,
 * and accepts all additional props supported by React Modal for further configuration.
 *
 * ## Basic Example
 * ```tsx
 * import React, { useState } from 'react';
 * import ModalWindow from './ModalWindow';
 *
 * const App = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   const toggleModal = () => setIsOpen(!isOpen);
 *
 *   return (
 *     <>
 *       <button onClick={toggleModal}>Open Modal</button>
 *       <ModalWindow
 *         isOpen={isOpen}
 *         setIsOpen={setIsOpen}
 *         appElementId="root"
 *       >
 *         <div>Modal Content Here</div>
 *       </ModalWindow>
 *     </>
 *   );
 * }
 * ```
 *
 * ## Advanced Usage
 * You can further customize the modal by directly passing props supported by React Modal.
 * This component adapts its z-index based on the device type (mobile or desktop) and allows for an
 * accessible close functionality.
 *
 * ```tsx
 * <ModalWindow
 *   isOpen={isOpen}
 *   setIsOpen={setIsOpen}
 *   appElementId="root"
 *   shouldCloseOnOverlayClick={true}
 * >
 *   <YourCustomComponent />
 * </ModalWindow>
 * ```
 *
 * # Props
 * - `isOpen`: Boolean indicating if the modal is open.
 * - `setIsOpen`: Function to set the open state of the modal.
 * - `children`: Content to be displayed within the modal.
 * - `appElementId`: ID of the app element to assist with accessibility.
 * - All other props are passed directly to the underlying React Modal component, allowing for extensive customization.
 *
 * # Design and Accessibility
 * This component automatically applies a `ScrollLock` when the modal is open to prevent background
 * scrolling and sets appropriate z-index values to ensure the modal is layered correctly on the page.
 * It also configures the modal for accessibility, including support for closing on ESC and setting
 * `aria-modal` to true.
 */
const ModalWindow: FunctionComponent<ModalWindowProps> = ({
  isOpen,
  setIsOpen,
  children,
  appElementId,
  ...modalProps
}): React.ReactElement => {
  const isMobile = useMobile();

  const customStyles: Modal.Styles = {
    overlay: {
      zIndex: isMobile ? MAX_Z_INDEX : zIndexes.overlay,
    },
    content: {
      zIndex: isMobile ? MAX_Z_INDEX : zIndexes.popup,
      background: 'none',
      border: 'none',
    },
  };

  const appElement =
    typeof window !== 'undefined' && typeof document.getElementById === 'function'
      ? document.getElementById(appElementId || 'app') || undefined
      : undefined;

  const closeModalHandler = (): void => {
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      style={customStyles}
      appElement={appElement}
      shouldCloseOnEsc
      onRequestClose={closeModalHandler}
      aria-modal="true"
      {...modalProps}
    >
      <Overlay>
        <GridItem
          columnStartDesktop={6}
          columnSpanDesktop={6}
          columnStartDevice={3}
          columnSpanDevice={10}
          columnStartSmallDevice={1}
          columnSpanSmallDevice={14}
        >
          <InnerModal>
            <CloseButton onClick={closeModalHandler} aria-label="Close modal window" tabIndex={0}>
              <Icon iconName="Close" />
            </CloseButton>
            <ContentWrapper>{children}</ContentWrapper>
          </InnerModal>
        </GridItem>
        <ScrollLock />
      </Overlay>
    </Modal>
  );
};

export default ModalWindow;
